import { createFileRoute } from "@tanstack/react-router";
import { buildSeo } from "@/lib/seo";
import { MarketingLayout, Eyebrow } from "@/components/marketing/Layout";
import { useHydratedTranslation } from "@/components/marketing/Layout";
import { MapPin, Mail, Globe2, ShieldCheck } from "lucide-react";

// Legal imprint. Required by §5 TMG in Germany and §5 ECG in Austria for any
// commercial site reachable there, and expected in Switzerland — our home
// market is German-speaking, so this page is not optional.
//
// The company facts are identical in every language, so only the labels are
// translated. Nothing here is invented: the address, owner and contact are the
// ones already published in our Organization schema.

const COMPANY = {
  project: "RealityTwin.io",
  legalName: "Ruznic Marketing",
  owner: "Ajdin Ruznic",
  street: "Leimgrubstrasse 5",
  postalCode: "8340",
  city: "Hinwil",
  country: "Schweiz / Switzerland",
  email: "hello@realitytwin.io",
  web: "realitytwin.io",
  operatorWeb: "https://ruznic-marketing.com",
};

export const Route = createFileRoute("/impressum")({
  head: () =>
    buildSeo({
      path: "/impressum",
      title: "Impressum — Reality Twin | Ruznic Marketing, Hinwil (CH)",
      description:
        "Impressum für Reality Twin: Ruznic Marketing, Leimgrubstrasse 5, 8340 Hinwil, Schweiz. Kontakt, Verantwortlichkeit und Haftungshinweise.",
    }),
  component: ImpressumPage,
});

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col sm:flex-row gap-1 sm:gap-4 py-3 border-b border-border last:border-b-0">
      <dt className="sm:w-56 shrink-0 text-[13px] text-muted-foreground">{label}</dt>
      <dd className="text-[15px] break-words">{children}</dd>
    </div>
  );
}

function ImpressumPage() {
  const t = useHydratedTranslation();

  return (
    <MarketingLayout>
      <section className="border-b border-border">
        <div className="mx-auto max-w-3xl px-6 pt-20 pb-12 sm:pt-28">
          <Eyebrow>{t("routes.impressum.eyebrow", { defaultValue: "Legal" })}</Eyebrow>
          <h1 className="mt-4 font-serif text-[40px] sm:text-[56px] leading-[1.02] tracking-[-0.03em]">
            {t("routes.impressum.title", { defaultValue: "Impressum" })}
          </h1>
          <p className="mt-5 text-[17px] leading-[1.6] text-muted-foreground">
            {t("routes.impressum.intro", {
              defaultValue:
                "Provider identification for realitytwin.io. Reality Twin is built and operated in Switzerland.",
            })}
          </p>
        </div>
      </section>

      <section className="py-14">
        <div className="mx-auto max-w-3xl px-6">
          {/* Who is responsible */}
          <div className="hairline rounded-2xl bg-card p-6 sm:p-8">
            <div className="font-mono-eyebrow mb-2">
              {t("routes.impressum.providerTitle", { defaultValue: "Provider" })}
            </div>
            <dl>
              <Row label={t("routes.impressum.project", { defaultValue: "Project" })}>
                {COMPANY.project}
              </Row>
              <Row label={t("routes.impressum.company", { defaultValue: "Operated by" })}>
                <a
                  href={COMPANY.operatorWeb}
                  className="underline hover:text-foreground transition"
                  rel="noopener"
                >
                  {COMPANY.legalName}
                </a>
              </Row>
              <Row label={t("routes.impressum.legalForm", { defaultValue: "Legal form" })}>
                {t("routes.impressum.soleProprietorship", {
                  defaultValue: "Sole proprietorship (Einzelunternehmen), Switzerland",
                })}
              </Row>
              <Row label={t("routes.impressum.responsible", { defaultValue: "Responsible" })}>
                {COMPANY.owner}
              </Row>
              <Row label={t("routes.impressum.address", { defaultValue: "Address" })}>
                <span className="inline-flex items-start gap-1.5">
                  <MapPin className="h-3.5 w-3.5 mt-1 shrink-0 text-muted-foreground" />
                  <span>
                    {COMPANY.street}
                    <br />
                    {COMPANY.postalCode} {COMPANY.city}
                    <br />
                    {COMPANY.country}
                  </span>
                </span>
              </Row>
              <Row label={t("routes.impressum.contact", { defaultValue: "Contact" })}>
                <a
                  href={`mailto:${COMPANY.email}`}
                  className="inline-flex items-center gap-1.5 underline hover:text-foreground transition"
                >
                  <Mail className="h-3.5 w-3.5 shrink-0" /> {COMPANY.email}
                </a>
              </Row>
              <Row label={t("routes.impressum.website", { defaultValue: "Website" })}>
                <span className="inline-flex items-center gap-1.5">
                  <Globe2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" /> {COMPANY.web}
                </span>
              </Row>
            </dl>
            {/* Stated rather than left blank: in Switzerland a sole
                proprietorship is only required to enter the commercial register
                above CHF 100'000 turnover, and VAT registration begins at the
                same threshold. Saying so is more transparent than an empty field. */}
            <p className="mt-4 text-[12.5px] leading-relaxed text-muted-foreground">
              {t("routes.impressum.registerNote", {
                defaultValue:
                  "As a Swiss sole proprietorship, entry in the commercial register and VAT registration are only mandatory above CHF 100'000 in annual turnover.",
              })}
            </p>
          </div>

          {/* Swiss jurisdiction — a trust primitive for agent-to-agent commerce,
              not a marketing badge. */}
          <div className="mt-6 hairline rounded-2xl bg-cream p-6 sm:p-8">
            <div className="flex items-start gap-3">
              <ShieldCheck className="h-5 w-5 shrink-0 mt-0.5" strokeWidth={1.75} />
              <div>
                <div className="font-serif text-[22px] leading-tight tracking-[-0.02em]">
                  {t("routes.impressum.swissTitle", {
                    defaultValue: "Built and operated in Switzerland",
                  })}
                </div>
                <p className="mt-2 text-[14.5px] leading-relaxed text-muted-foreground">
                  {t("routes.impressum.swissBody", {
                    defaultValue:
                      "Reality Twin is a Swiss product: developed in Hinwil, Zurich, operated by a Swiss company and governed by Swiss law, with Hinwil as the place of jurisdiction. Application data is hosted in the European Union (Ireland) and covered by the GDPR and the Swiss FADP, with a Data Processing Addendum available to every customer.",
                  })}
                </p>
              </div>
            </div>
          </div>

          {/* Standard legal sections */}
          <div className="mt-10 space-y-8 text-[15px] leading-[1.7] text-muted-foreground">
            <div>
              <h2 className="font-serif text-[24px] text-foreground mb-2 tracking-[-0.02em]">
                {t("routes.impressum.liabilityTitle", { defaultValue: "Liability for content" })}
              </h2>
              <p>
                {t("routes.impressum.liabilityBody", {
                  defaultValue:
                    "We prepare the content of this site with care, but assume no liability for its accuracy, completeness or timeliness. Answers generated by AI Twins are informational and non-binding unless a company has explicitly authorised binding statements.",
                })}
              </p>
            </div>
            <div>
              <h2 className="font-serif text-[24px] text-foreground mb-2 tracking-[-0.02em]">
                {t("routes.impressum.linksTitle", { defaultValue: "Liability for links" })}
              </h2>
              <p>
                {t("routes.impressum.linksBody", {
                  defaultValue:
                    "Our site links to external websites over whose content we have no influence. The respective provider is always responsible for the content of linked pages.",
                })}
              </p>
            </div>
            <div>
              <h2 className="font-serif text-[24px] text-foreground mb-2 tracking-[-0.02em]">
                {t("routes.impressum.copyrightTitle", { defaultValue: "Copyright" })}
              </h2>
              <p>
                {t("routes.impressum.copyrightBody", {
                  defaultValue:
                    "The content and works on these pages are protected by copyright. Reproduction, editing or distribution beyond the limits of copyright law requires our written consent.",
                })}
              </p>
            </div>
            <div>
              <h2 className="font-serif text-[24px] text-foreground mb-2 tracking-[-0.02em]">
                {t("routes.impressum.disputeTitle", { defaultValue: "Dispute resolution" })}
              </h2>
              <p>
                {t("routes.impressum.disputeBody", {
                  defaultValue:
                    "We are neither obliged nor willing to participate in dispute resolution proceedings before a consumer arbitration board. For any question or complaint, write to us directly — we answer every message.",
                })}
              </p>
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
